import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const VisitorAnalytics = ({ visitors, onExport }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGallery, setFilterGallery] = useState('all');

  const filteredVisitors = visitors?.filter(visitor => {
    const matchesSearch = visitor?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         visitor?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesGallery = filterGallery === 'all' || visitor?.galleryAccessed === filterGallery;
    return matchesSearch && matchesGallery;
  });

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const uniqueGalleries = [...new Set(visitors.map(v => v.galleryAccessed))];

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-80">
            <Icon 
              name="Search" 
              size={20} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
            />
            <input
              type="text"
              placeholder="Search visitors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 bg-muted border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          
          <select
            value={filterGallery}
            onChange={(e) => setFilterGallery(e?.target?.value)}
            className="px-4 py-2 bg-muted border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Galleries</option>
            {uniqueGalleries?.map((gallery, index) => (
              <option key={index} value={gallery}>{gallery}</option>
            ))}
          </select>
        </div>
        
        <Button variant="outline" iconName="Download" iconPosition="left" onClick={onExport}>
          Export Data
        </Button>
      </div>
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Visitor</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contact</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Gallery</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Downloads</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Visit</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Total Visits</th>
            </tr>
          </thead>
          <tbody>
            {filteredVisitors?.map((visitor) => (
              <tr key={visitor?.id} className="border-b border-border hover:bg-muted/50 smooth-transition">
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <Image
                      src={visitor?.avatar}
                      alt={visitor?.avatarAlt}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium">{visitor?.name}</p>
                      <p className="text-xs text-muted-foreground">ID: {visitor?.id}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="space-y-1">
                    <p className="text-sm flex items-center gap-2">
                      <Icon name="Mail" size={14} color="var(--color-muted-foreground)" />
                      {visitor?.email}
                    </p>
                    <p className="text-sm flex items-center gap-2">
                      <Icon name="Phone" size={14} color="var(--color-muted-foreground)" />
                      {visitor?.phone}
                    </p>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <p className="text-sm">{visitor?.galleryAccessed}</p>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Download" size={16} color="var(--color-primary)" />
                    <span className="text-sm data-text">{visitor?.downloadCount}</span>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <p className="text-sm">{formatDate(visitor?.lastVisit)}</p>
                </td>
                <td className="py-4 px-4">
                  <span className="text-sm data-text">{visitor?.totalVisits}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden space-y-3">
        {filteredVisitors?.map((visitor) => (
          <div key={visitor?.id} className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-start gap-3 mb-3">
              <Image
                src={visitor?.avatar}
                alt={visitor?.avatarAlt}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium mb-1">{visitor?.name}</h4>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p className="flex items-center gap-2">
                    <Icon name="Mail" size={12} />
                    {visitor?.email}
                  </p>
                  <p className="flex items-center gap-2">
                    <Icon name="Phone" size={12} />
                    {visitor?.phone}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 mb-3 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Gallery</span>
                <span className="font-medium">{visitor?.galleryAccessed}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Downloads</span>
                <span className="font-medium data-text">{visitor?.downloadCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total Visits</span>
                <span className="font-medium data-text">{visitor?.totalVisits}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Last Visit</span>
                <span className="font-medium">{formatDate(visitor?.lastVisit)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VisitorAnalytics;